import { CookieUtil } from '../CookieUtil';
import * as en_texts from './i18n.en.json';
import * as es_texts from './i18n.es.json';
import * as de_texts from './i18n.de.json';
import languageCodeMapping from './iso-language-codes.json';
import globalStore from '../../stores/globalStore';
import { LanguageCode } from '../../models/LanguageCode';
import { DefaultLanguages } from '../../models/DefaultLanguages';
import { Language } from '../../models/Language';
import { EventEmitter } from '@stencil/core';

const cookieUtil = CookieUtil;

export const emitLanguageEvent = (
  eventEmitter: EventEmitter,
  languages: {activeLanguage: Language, detectedLanguage: Language}
) => {
  eventEmitter.emit({
    activeLanguage: removeKey(languages.activeLanguage, "href"),
    detectedLanguage: removeKey(languages.detectedLanguage, "href"),
  });
}

const removeKey = <T extends object, K extends keyof T>(obj: T, key: K): Omit<T, K> => {
  const newObj = {...obj}
  delete newObj[key];
  return newObj;
}

export class I18nUtil {
  private static readonly I18N = {
    en: en_texts,
    es: es_texts,
    de: de_texts,
  };

  /**
   * Returns the local name of a language from its language code
   * @param langCode The two-letter language code (e.g., 'en', 'de', 'es')
   * @returns The local name of the language (e.g., 'English', 'Deutsch', 'Español')
   * or the language code itself if no mapping is found
   */
  public static mapLangCodeToLocalName(langCode: LanguageCode): string {
    if (!langCode) {
      return null;
    }

    const normalizedCode = langCode.toLowerCase() as LanguageCode;
    return (languageCodeMapping[normalizedCode] && languageCodeMapping[normalizedCode].local) || normalizedCode; //return language code if no mapping is found
  }

  public static getTranslation(key: string) {
    const currentLang = globalStore.state.language.toLowerCase();
    const value = this.I18N[currentLang][key];
    if (!value) {
      return '';
    }
    return value;
  }

  public static setLanguages(
    hfConfigLanguages: Language[],
    alternateLinksLanguages: Language[],
    useDetectedLanguage: boolean = false,
    prioritizeLangAttr: boolean = false,
  ): {
    activeLanguage: Language,
    detectedLanguage: Language
  } {
    const determinedLanguageCode: LanguageCode = this.determineLanguageCode(prioritizeLangAttr);

    const activeLanguage = this.getActiveLanguageByLanguageCode(determinedLanguageCode, [...hfConfigLanguages, ...alternateLinksLanguages]); // Bekannte Sprache für Header
    const detectedLanguage = this.getDetectedLanguageByLanguageCode(determinedLanguageCode, hfConfigLanguages, alternateLinksLanguages);  // Sprache, die ermittelt wurde (Page-Sprache)

     // Setze Cookie für die gewählte Sprache, entweder einer bekannten Header Sprache oder der ermittelten Sprache, falls dies explizit gewünscht ist
    this.setLanguageCookie(useDetectedLanguage? detectedLanguage.code : activeLanguage.code);
    return {
      activeLanguage,
      detectedLanguage,
    };
  }

  private static getLanguageCodeFromCookie(): LanguageCode {
    const cookieLang = cookieUtil.getCookieValue('bahf_lang');
    return cookieLang.toLowerCase() as LanguageCode;
  }

  private static getLanguageCodeFromLangAttribute(): LanguageCode {
    const htmlLangValue = document.documentElement.lang;
    return htmlLangValue.toLowerCase() as LanguageCode;
  }

  public static setLanguageCookie(languageCode: LanguageCode) {
    const sekProTag = 86400;
    const tage = 30;
    const multiplier = 1000;

    const maxAge = tage * sekProTag;
    const expiresDate = Date.now() + tage * sekProTag * multiplier;
    CookieUtil.setCookie('bahf_lang', languageCode.toLowerCase(), maxAge, new Date(expiresDate));
  }

  private static determineLanguageCode(
    prioritizeLangAttr: boolean = false
  ): LanguageCode {
    /*URL-Query-Parameter (?lang=de)*/
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang') ;
    if (this.isLanguageCode(langParam)) {
      return langParam.toLowerCase() as LanguageCode;
    }

    /*URL-Pfad-Parameter (/en/..)*/
    const langPath = this.getLanguageCodeFromPath();
    if(this.isLanguageCode(langPath)) {
      return langPath.toLowerCase() as LanguageCode;
    }

    if (prioritizeLangAttr) {
      /*HTML-Attribut (lang="de")*/
      const langAttribute = this.getLanguageCodeFromLangAttribute();
      if(this.isLanguageCode(langAttribute)) {
        return langAttribute;
      }
    }

    /*Language-Cookie*/
    const langCookie = this.getLanguageCodeFromCookie();
    if(this.isLanguageCode(langCookie)) {
      return langCookie;
    }

    if (!prioritizeLangAttr) {
      /*HTML-Attribut (lang="de")*/
      const langAttribute = this.getLanguageCodeFromLangAttribute();
      if(this.isLanguageCode(langAttribute)) {
        return langAttribute;
      }
    }

    /*Default*/
    return DefaultLanguages.de.code;
  }

  private static getActiveLanguageByLanguageCode(code: LanguageCode, providedLanguages: Language[]): Language {
    // Die Sprache für den Header (activeLanguage) kann nur "de", "en" oder "es" sein und
    // das auch nur, wenn die entsprechende Sprache via hfconfig-Attribut oder Alternatelinks
    // explizit von der Anwendung bereitgestellt wird.
    const overlappingLanguages: Language[] = I18nUtil.providedLanguagesWithDefaultLanguagesOverlap(providedLanguages);
    return overlappingLanguages.find(l => l.code === code) ?? DefaultLanguages[DefaultLanguages.de.code];
  }

  private static getDetectedLanguageByLanguageCode(
    code: LanguageCode,
    languages: Language[],
    alternateLinks: Language[],
  ): Language {
    let language: Language;

    if (languages && languages.length > 0) {
      language = languages.find(
        (lang) => lang.code.toLowerCase() === code.toLowerCase()
      );
    } else {
      language = alternateLinks?.find(
        (lang) => lang.code.toLowerCase() === code.toLowerCase()
      );
    }

    return language || { ...DefaultLanguages.de };
  }

  public static isLanguageCode (value: string): value is LanguageCode {
    return (
      typeof value === 'string' &&
      value.length === 2 &&
      !!languageCodeMapping[value.toLowerCase()]
    );
  }

  /**
   * Extracts a language code from any segment in the URL path
   * Supports two-character codes (like /de/)
   * @returns The found language code or null if none is found
   */
  private static getLanguageCodeFromPath(): LanguageCode {
    const pathSegments = window.location.pathname.split('/').filter(segment => segment);

    if (!pathSegments || pathSegments.length === 0) {
      return null;
    }

    // Check all segments for a valid language code
    for (const segment of pathSegments) {
      // Check if it's a valid language code (either format)
      if (this.isLanguageCode(segment)) {
        return segment.toLowerCase() as LanguageCode;
      }
    }

    return null;
  }

  private static providedLanguagesWithDefaultLanguagesOverlap(providedLanguages: Language[]): Language[] {
    if (!providedLanguages) return [];
    const codes = new Set(providedLanguages.map(language => language.code.toLowerCase()));
    return Object.values(DefaultLanguages).filter(language => codes.has(language.code))
  }
}
